import { type ReactNode, useEffect } from 'react';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useQuery } from '@tanstack/react-query';
import { getCurrentUserApi } from '@/features/auth/services/auth.api';
import { LoadingScreen } from '@/shared/components/common/LoadingScreen';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { status, setUser, setStatus, clearSession } =
    useAuthStore();

  // ─── Session Restoration ──────────────────────────────────────────
  //
  // On app load:
  // 1. Sets status to 'loading' to prevent ProtectedRoute from
  //    redirecting to /login before auth is determined.
  // 2. Fires GET /auth/me (with or without a stored access token).
  // 3. If token is expired/missing, the 401 interceptor automatically
  //    POSTs to /auth/refresh using the HttpOnly cookie.
  // 4. On refresh success: new access token stored and /auth/me retries.
  // 5. On failure after all retries: set status to 'unauthenticated'.
  //
  // CRITICAL: clearSession() must NOT be called inside the queryFn's
  //   catch block. Doing so sets status to 'unauthenticated' WHILE the
  //   React Query retry is pending (after the first failure but before
  //   the retry fires). This causes ProtectedRoute to see 'unauthenticated'
  //   and redirect to /login BEFORE the retry can complete.
  //
  // PREVIOUS BUG: The original fix used `useIsFetching()` to detect
  //   whether retries were still in-flight. However, during the retry
  //   DELAY (the 1-second wait between a failed attempt and the retry),
  //   `useIsFetching` returns 0 — the query is NOT currently fetching.
  //   This caused the deferred `useEffect` to fire `clearSession()` in
  //   that 1-second gap, setting status to 'unauthenticated' before the
  //   retry could execute.
  //
  // CORRECT FIX: Use `failureCount` from the query result. With
  //   `retry: 1`, `failureCount` reaches 2 only after BOTH the initial
  //   attempt AND the retry have failed. During the retry DELAY,
  //   `failureCount` stays at 1, so the effect does NOT fire.

  const { isError, isFetched, failureCount } = useQuery({
    queryKey: ['auth', 'init'],
    queryFn: async () => {
      setStatus('loading');
      const data = await getCurrentUserApi();
      setUser(data.user);
      setStatus('authenticated');
      return data;
    },
    // Milestone 2.3.6 — keep the query ENABLED while it is in-flight.
    // The queryFn itself calls setStatus('loading'), which used to flip
    // `enabled` to false mid-flight; React Query can then abort/pause the
    // session-restore request (and its 401 → cookie-refresh → retry cycle),
    // causing a valid session to appear unauthenticated on refresh and
    // redirecting the user to /login. Including 'loading' keeps the query
    // enabled through the whole restore cycle.
    enabled: status === 'idle' || status === 'loading',
    retry: 1,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
  });

  // ─── Deferred Session Clear ──────────────────────────────────────
  //
  // Only call clearSession() after ALL retries are exhausted.
  // With retry: 1, failureCount reaches 2 only after both the
  // initial attempt and the retry have failed.
  //
  // This is the CORRECT approach — unlike `useIsFetching`,
  // `failureCount` does NOT drop to 0 during the retry delay.
  // It monotonically increases with each failure, guaranteeing
  // that clearSession() only fires after the last retry is done.

  useEffect(() => {
    if (isError && isFetched && failureCount >= 2) {
      clearSession();
    }
  }, [isError, isFetched, failureCount, clearSession]);

  // ─── Hard Timeout Safety Net ───────────────────────────────────────
  //
  // If session restoration hasn't resolved either way within 8s, force
  // 'unauthenticated' so the user reaches the login page instead of being
  // stuck on LoadingScreen forever. This is a backstop for scenarios where
  // the query never settles at all (e.g. a paused/stuck fetch from a
  // network partition or environment quirk) — failureCount-based clearing
  // above only fires once the query actually reports a failure, which
  // never happens if the request itself never resolves.
  useEffect(() => {
    if (status !== 'idle' && status !== 'loading') return;
    const timer = setTimeout(() => {
      const current = useAuthStore.getState().status;
      if (current === 'idle' || current === 'loading') {
        clearSession();
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [status, clearSession]);

  // Show global loading during initial auth determination.
  // Both 'idle' (before the query fires) and 'loading' (while
  // the query + interceptor refresh + retries are in flight).
  if (status === 'idle' || status === 'loading') {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}
